/* ═══ AgAlert — Anagogia Design System ═══ */

import { AG_LABELS } from '../utils/agLabels';

import styles from './AgAlert.module.css';

interface AgAlertProps {
  /** Semantic severity — determines color and default icon */
  severity: 'success' | 'warning' | 'error' | 'info';
  /** Visual style */
  variant?: 'filled' | 'outlined' | 'standard';
  /** Show close button; called on click */
  onClose?: () => void;
  /** Override the default severity icon */
  icon?: React.ReactNode;
  /** Alert content */
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

/* ── Default SVG icons per severity ── */

const SuccessIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
      clipRule="evenodd"
    />
  </svg>
);

const WarningIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
      clipRule="evenodd"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
      clipRule="evenodd"
    />
  </svg>
);

const InfoIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
      clipRule="evenodd"
    />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
    <line x1="4" y1="4" x2="12" y2="12" />
    <line x1="12" y1="4" x2="4" y2="12" />
  </svg>
);

const DEFAULT_ICONS: Record<AgAlertProps['severity'], React.ReactNode> = {
  success: <SuccessIcon />,
  warning: <WarningIcon />,
  error: <ErrorIcon />,
  info: <InfoIcon />,
};

/**
 * Message d'alerte info/success/warning/error, fermable.
 * @category Feedback
 */
function AgAlert({
  severity,
  variant = 'standard',
  onClose,
  icon,
  children,
  className,
  style,
}: AgAlertProps) {
  const rootClass = [
    styles.alert,
    styles[variant],
    styles[severity],
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const renderedIcon = icon !== undefined ? icon : DEFAULT_ICONS[severity];

  return (
    <div className={rootClass} role="alert" style={style}>
      {renderedIcon && <span className={styles.icon}>{renderedIcon}</span>}

      <div className={styles.message}>{children}</div>

      {onClose && (
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label={AG_LABELS.fermerMessage}
        >
          <CloseIcon />
        </button>
      )}
    </div>
  );
}

export { AgAlert };
export type { AgAlertProps };
export default AgAlert;
